import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Chip, Divider, Stack, TextField, Button,
  Switch, FormControlLabel, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Build as BuildIcon, Cloud as CloudIcon, Lock as LockIcon,
  CheckCircleOutline as AllowIcon, Block as BlockIcon,
} from '@mui/icons-material';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import { EntityListPanel, type EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import { useAuth } from '../../auth/context/AuthContext';
import { DOMAIN_LABELS, GOVERNANCE, GLOBAL_POLICIES, REQUIRED_LEVEL_LABEL, appliedLevelLabel, requiredLevelOf } from '../catalog';
import { toolCatalogStore } from '../services/toolCatalogService';
import { useStoredToolAccess, useToolConfigs, useUpsertToolConfig } from '../hooks/useToolConfigs';
import type { GoverningPolicy, ToolCatalogEntry } from '../types';
import { roleGateOf, roleGateSummary } from '../utils/roleGate';
import ToolKinds from './ToolKinds';
import ToolAccessPanel from './ToolAccessPanel';
import ActionAccessList from './ActionAccessList';
import StoredReadFailed from './StoredReadFailed';

const groupByDomain = (e: ToolCatalogEntry) => [{ key: e.domain, label: DOMAIN_LABELS[e.domain] ?? e.domain }];


interface ToolManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ToolManagerModal = memo<ToolManagerModalProps>(({ isOpen, onClose }) => {
  const { isAdmin } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Decision 9: agent tools carry kind marks and access; every other action carries access only.
  const [view, setView] = useState<'tools' | 'actions'>('tools');
  // A failed read shows no level, never the shipped one in its place (FE-14).
  const { data: storedAccess, isError: storedReadFailed } = useStoredToolAccess(isOpen);
  const levelLabel = useCallback((actionId: string): string | null => (storedReadFailed ? null : appliedLevelLabel(
    REQUIRED_LEVEL_LABEL[requiredLevelOf(actionId)], requiredLevelOf(actionId) === 'admin-role', storedAccess?.[actionId])),
  [storedAccess, storedReadFailed]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: overrides = {} } = useToolConfigs(isOpen);
  const upsert = useUpsertToolConfig();
  const saving = upsert.isPending;

  const entries = toolCatalogStore.getAll();
  const selected = useMemo(() => entries.find((e) => e.actionId === selectedId) ?? null, [entries, selectedId]);
  const govRows = useMemo<GoverningPolicy[]>(() => (selected ? GOVERNANCE[selected.actionId] ?? [] : []), [selected]);
  const roleGate = roleGateOf(govRows);

  const override = selected ? overrides[selected.actionId] : undefined;
  const effectiveDescription = override?.description_override ?? selected?.description ?? '';
  const dirty = Boolean(selected) && draft !== effectiveDescription;
  const enabled = override?.enabled !== false; // undefined/true = enabled; only false disables

  // List reflects the overlay: tag edited/disabled tools and tint disabled rows.
  const listConfig = useMemo<EntityListItemConfig<ToolCatalogEntry>>(() => ({
    getId: (e) => e.actionId,
    getPrimaryText: (e) => e.name,
    getSecondaryText: (e) => {
      const ov = overrides[e.actionId];
      const level = levelLabel(e.actionId);
      const parts = [DOMAIN_LABELS[e.domain] ?? e.domain, ...(level ? [level] : [])];
      if (ov?.enabled === false) parts.push('disabled');
      else if (ov?.description_override) parts.push('edited');
      return parts.join(' · ');
    },
    getSearchText: (e) => `${e.name} ${e.actionId} ${e.description}`,
    getBackgroundColor: (e) => (overrides[e.actionId]?.enabled === false ? 'action.disabledBackground' : undefined),
    secondaryTypographyProps: { variant: 'caption' },
  }), [overrides, levelLabel]);

  useEffect(() => {
    setDraft(effectiveDescription);
    setError(null);
  }, [selectedId, effectiveDescription]);

  const save = async () => {
    if (!selected) return;
    setError(null);
    try {
      await upsert.mutateAsync({ actionId: selected.actionId, update: { description_override: draft } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  // PS-UC-23 4a: back to the code's description; the server drops the override.
  const resetDescription = async () => {
    if (!selected) return;
    setError(null);
    try {
      await upsert.mutateAsync({ actionId: selected.actionId, update: { reset_description: true } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    }
  };

  const toggleEnabled = async (next: boolean) => {
    if (!selected) return;
    setError(null);
    try {
      await upsert.mutateAsync({ actionId: selected.actionId, update: { enabled: next } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}>
      <ModalHeader icon={BuildIcon} title="Tool Manager" onClose={onClose} desktopRecommended />

      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, next) => next && setView(next)}>
          <ToggleButton value="tools">Agent tools</ToggleButton>
          <ToggleButton value="actions">Actions</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {view === 'actions' ? (
        <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
          <ActionAccessList isOpen={isOpen} editable={isAdmin} />
        </DialogContent>
      ) : (
      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        <Box sx={{ width: 320, borderRight: 1, borderColor: 'divider', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
          <EntityListPanel
            entities={entries}
            selectedEntity={selected}
            isNew={false}
            saving={false}
            config={listConfig}
            emptyStateText="No tools"
            emptyStateHelperText=""
            onSelect={(e) => setSelectedId(e.actionId)}
            searchable
            groupBy={groupByDomain}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {!selected ? (
            <EmptyStateMessage icon={BuildIcon} title="Select a tool" description="Pick a tool to inspect its instruction, permission, and Cedar governance. Admins can edit the agent-facing instruction; everything else is generated from the code-defined ActionRegistry." />
          ) : (
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {selected.aws && <CloudIcon sx={{ fontSize: 18, color: 'info.main' }} />}
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{selected.name}</Typography>
                {selected.protected && (
                  <Chip icon={<LockIcon sx={{ fontSize: 13 }} />} label="protected" size="small" variant="outlined" />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">Code-defined (not editable)</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label={`Cedar: ${selected.actionId}`} size="small" sx={{ fontFamily: 'monospace' }} />
                <Chip label={`domain: ${selected.domain}`} size="small" variant="outlined" />
                {selected.aws && <Chip label="AWS-native" size="small" color="info" variant="outlined" />}
              </Stack>

              {isAdmin ? (
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={<Switch size="small" checked={enabled} onChange={(e) => toggleEnabled(e.target.checked)} />}
                  label={
                    <Typography variant="body2">
                      Enabled for agents
                      {!enabled && (
                        <Typography component="span" variant="caption" color="error.main"> — disabled, hidden from agents</Typography>
                      )}
                    </Typography>
                  }
                />
              ) : (!enabled && (
                <Chip label="disabled for agents" size="small" color="error" variant="outlined" sx={{ mb: 1 }} />
              ))}

              <ToolKinds actionId={selected.actionId} stored={override?.kinds} editable={isAdmin} />

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2">Instruction (agent-facing)</Typography>
                {override && <Chip label="overridden" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                What the agent reads to decide when to use this tool.
                {override && ` Overridden by ${override.updated_by ?? 'an admin'}.`}
              </Typography>

              {isAdmin ? (
                <>
                  <TextField
                    fullWidth multiline minRows={4} size="small"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                    <Button size="small" variant="contained" disabled={!dirty || saving} onClick={save}>
                      {saving ? 'Saving…' : 'Save instruction'}
                    </Button>
                    {override?.description_override && (
                      <Button size="small" disabled={saving} onClick={resetDescription}>Reset to original</Button>
                    )}
                    {dirty && !saving && <Typography variant="caption" color="warning.main">Unsaved changes</Typography>}
                    {error && <Typography variant="caption" color="error.main">{error}</Typography>}
                  </Box>
                </>
              ) : (
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre-wrap' }}>
                  <Typography variant="body2">{effectiveDescription}</Typography>
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>Permission envelope</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Required level: the operator level a chat agent's call needs, from the Cedar policies. Cedar decides each call at run time.
              </Typography>
              <Box data-testid="required-level">
                {storedReadFailed ? <StoredReadFailed /> : <Chip label={levelLabel(selected.actionId)} size="small" />}
              </Box>
              {selected.standalone && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                    Standalone: not on the read/write/admin ladder. Authorization is decided by the policies below.
                  </Typography>
                  <Chip label="standalone" size="small" color="warning" variant="outlined" />
                </>
              )}
              <Box sx={{ mt: 1.5 }}>
                <ToolAccessPanel
                  actionId={selected.actionId} editable={isAdmin} roleGated={Boolean(roleGate)}
                  shippedLevel={REQUIRED_LEVEL_LABEL[requiredLevelOf(selected.actionId)]}
                />
              </Box>
              {selected.aws && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                  Note: read/write for AWS-native tools is set per-package (the usage), not here — this tool can reach any API of the granted tier.
                </Typography>
              )}

              <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>
                Governing policies{govRows.length ? ` (${govRows.length})` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                base_policies.cedar rules that decide this tool at call time.{' '}
                {roleGate
                  ? roleGateSummary(roleGate)
                  : selected.policyGoverned
                    ? 'Referenced by action_id directly: bespoke authorization.'
                    : 'Granted via its level group.'}
              </Typography>
              {govRows.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No tool-specific policy — only the global walls below apply.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {govRows.map((p) => (
                    <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      {p.effect === 'forbid'
                        ? <BlockIcon sx={{ fontSize: 15, color: 'error.main' }} />
                        : <AllowIcon sx={{ fontSize: 15, color: 'success.main' }} />}
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.id}</Typography>
                      {p.principals.map((pr) => (
                        <Chip key={pr} label={pr} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      ))}
                      {p.via.startsWith('group:') && (
                        <Chip label="via group" size="small" sx={{ height: 18, fontSize: 10 }} />
                      )}
                      {p.conditionHint && (
                        <Typography variant="caption" color="text.secondary">· {p.conditionHint}</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                + {GLOBAL_POLICIES.length} broad walls apply globally (JIT, super-admin and the other cross-cutting rules).
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      )}

      <ModalFooter count={entries.length} label="tools" onClose={onClose} py={1.5} buttonSize="small" />
    </Dialog>
  );
});

ToolManagerModal.displayName = 'ToolManagerModal';

export default ToolManagerModal;
